import React, { useEffect } from "react";
import { Modal, Loader, LoaderSizes } from "@hid-galaxy-ui/galaxy-react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../../../../redux";
import { getTestCaseSagaAction, extractNumericId } from "../../../../../../../../redux/saga/project-validation/saga-actions";
import { projectValidationSliceAction } from "../../../../../../../../redux/slices/project-validation";
import "./Modals.scss";

interface TestCaseDetailsModalProps {
  testCaseId: string;
  onClose: () => void;
}

// Helper function to format test case ID with leading zeros
const formatTestCaseId = (testCaseId: string): string => {
  const num = parseInt(testCaseId, 10);
  if (isNaN(num)) return `TC-${testCaseId}`;
  // Format with leading zero for single digits (01-09)
  return `TC-${num.toString().padStart(2, "0")}`;
};

const TestCaseDetailsModal: React.FC<TestCaseDetailsModalProps> = ({
  testCaseId,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { selectedTestCase, testCases, status } = useAppSelector(
    (state) => state.project_validation
  );

  // Fetch test case data when modal opens or testCaseId changes
  useEffect(() => {
    if (testCaseId) {
      // Extract numeric ID if it's in formatted form (TC-01 -> 01 or "3" -> "3")
      const numericId = extractNumericId(testCaseId);

      // Try to find the test case in the Redux slice first
      const foundTestCase = testCases.find((tc) => {
        const tcId = extractNumericId(tc.ID);
        return tcId === numericId || tc.ID === testCaseId || tc.ID === numericId;
      });

      if (foundTestCase) {
        // Test case found in slice, set it directly
        dispatch(
          projectValidationSliceAction.setSelectedTestCase(foundTestCase)
        );
      } else {
        // Test case not found in slice, fetch from API
        dispatch(getTestCaseSagaAction(numericId));
      }
    }

    // Cleanup: clear selectedTestCase when modal closes
    return () => {
      dispatch(projectValidationSliceAction.setSelectedTestCase(null));
    };
  }, [testCaseId, dispatch, testCases]);

  return (
    <Modal
      headerText={`Test case: ${testCaseId ? formatTestCaseId(testCaseId) : ""}`}
      width={1000}
      onClose={onClose}
      className="test-case-details-modal"
    >
      <div className="modal-content">
        {status.testCaseLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "24px",
            }}
          >
            <Loader variant={LoaderSizes.large} />
          </Box>
        ) : selectedTestCase ? (
          <TableContainer component={Paper} className="test-case-table">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Test Case ID</TableCell>
                  <TableCell>High Level Scenarios</TableCell>
                  <TableCell>Precondition</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Expected Result</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{selectedTestCase.test_case_id || "N/A"}</TableCell>
                  <TableCell className="pre-wrap">
                    {selectedTestCase.high_level_scenario || "N/A"}
                  </TableCell>
                  <TableCell className="pre-wrap">
                    {selectedTestCase.pre_conditions || "N/A"}
                  </TableCell>
                  <TableCell className="pre-wrap">
                    {selectedTestCase.description || "N/A"}
                  </TableCell>
                  <TableCell className="pre-wrap">
                    {selectedTestCase.expected_result || "N/A"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        selectedTestCase.test_case_status === "Active" ||
                        selectedTestCase.test_case_status === "active"
                          ? "Active"
                          : "Inactive"
                      }
                      className={`status-chip ${(
                        selectedTestCase.test_case_status || ""
                      ).toLowerCase()}`}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ padding: "24px", textAlign: "center" }}>
            No test case data available
          </Box>
        )}
      </div>
    </Modal>
  );
};

export default TestCaseDetailsModal;
